// Photos card (asset-detail-revamp-handover §3.7): always three 4:3 slots. A photo opens full size; its delete button
// follows the existing rule (a manager, or whoever added it — R2). The first empty slot adds a photo (the existing
// flow: made at most 1600 px on this device, then add_asset_photo), the rest wait. The tag mounting photo is not one
// of the three — it lives on the Asset tag card.
import { canRemovePhoto, PHOTO_MAX_PER_ASSET } from '@fleetspoke/core/photos';
import AddAPhotoIcon from '@mui/icons-material/AddAPhotoOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  ButtonBase,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '../../auth/session';
import { type ShownPhoto, useAddAssetPhoto, useRemoveAssetPhoto } from '../../data/photos';
import { ImageDecodeError } from '../../lib/images';
import { Card, CardHeader } from './Card';

const slot = { aspectRatio: '4 / 3', borderRadius: 1.5, width: '100%' } as const;

export function PhotoSlots({
  assetId,
  companyId,
  photos,
  loading,
}: {
  assetId: string;
  companyId: string;
  photos: ShownPhoto[];
  loading: boolean;
}) {
  const { t } = useTranslation();
  const { me } = useSession();
  const add = useAddAssetPhoto(companyId, assetId);
  const remove = useRemoveAssetPhoto(assetId);
  const input = useRef<HTMLInputElement>(null);
  const addError = add.error as (Error & { code?: string }) | null;
  const empty = Math.max(0, PHOTO_MAX_PER_ASSET - photos.length);

  return (
    <Card id="asset-photos" data-testid="asset-photos">
      <CardHeader
        title={
          <>
            {t('photos.title')}{' '}
            <Typography component="span" variant="body2" color="text.secondary">
              {t('photos.count', { count: photos.length, max: PHOTO_MAX_PER_ASSET })}
            </Typography>
          </>
        }
        aside={
          <Typography variant="caption" color="text.secondary">
            {t('asset.photos_resized')}
          </Typography>
        }
      />
      {/* thumbnails, not a gallery: at most 160 px wide each (owner, 2026-09-24 — full-width slots were too big) */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 160px))', gap: 1.5 }}>
        {photos.map((p, i) => (
          <Box
            key={p.path}
            sx={{
              ...slot,
              position: 'relative',
              overflow: 'hidden',
              border: 1,
              borderColor: 'divider',
              bgcolor: 'action.hover',
              '& .photo-remove': { opacity: { xs: 1, md: 0 } },
              '&:hover .photo-remove, &:focus-within .photo-remove': { opacity: 1 },
            }}
          >
            <ButtonBase
              component="a"
              href={p.url ?? undefined}
              target="_blank"
              rel="noreferrer"
              aria-label={t('photos.photo_n', { n: i + 1 })}
              sx={{ width: '100%', height: '100%' }}
            >
              {p.url ? (
                <Box
                  component="img"
                  src={p.url}
                  alt={t('photos.photo_n', { n: i + 1 })}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              ) : null}
            </ButtonBase>
            {canRemovePhoto(p, me) ? (
              <Tooltip title={t('photos.remove')}>
                <IconButton
                  className="photo-remove"
                  size="small"
                  aria-label={t('photos.remove')}
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(p.path)}
                  sx={{
                    position: 'absolute',
                    top: 6,
                    right: 6,
                    bgcolor: 'background.paper',
                    '&:hover': { bgcolor: 'background.paper' },
                    transition: 'opacity 120ms',
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
          </Box>
        ))}
        {Array.from({ length: empty }, (_, i) =>
          i === 0 ? (
            <ButtonBase
              key="add"
              onClick={() => input.current?.click()}
              disabled={loading || add.isPending}
              data-testid="photo-add"
              aria-label={t('photos.add')}
              sx={{
                ...slot,
                border: '1px dashed',
                borderColor: 'primary.main',
                color: 'primary.main',
                flexDirection: 'column',
                gap: 0.5,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              {add.isPending ? (
                <CircularProgress size={28} aria-label={t('photos.uploading')} />
              ) : (
                <>
                  <AddAPhotoIcon />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {t('photos.add')}
                  </Typography>
                </>
              )}
            </ButtonBase>
          ) : (
            <Box
              key={`empty-${i}`}
              aria-hidden
              sx={{
                ...slot,
                border: '1px dashed',
                borderColor: 'divider',
                bgcolor: 'action.hover',
              }}
            />
          ),
        )}
      </Box>
      <input
        ref={input}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        data-testid="photo-input"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) add.mutate(file);
        }}
      />
      {addError ? (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {addError instanceof ImageDecodeError
            ? t('photos.not_image')
            : /at most 3/.test(addError.message)
              ? t('photos.full')
              : addError.message}
        </Alert>
      ) : null}
      {remove.error ? (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {(remove.error as { code?: string }).code === '42501'
            ? t('photos.not_yours')
            : (remove.error as Error).message}
        </Alert>
      ) : null}
    </Card>
  );
}
